// Mid-season report card projection: the report-card engine run on the
// season-to-date record extrapolated to a full schedule — "your grade if the
// season ended at this pace." Powers the challenge badge's projected grade and
// the on-demand Progress Report.
//
// Awards and the lottery slot don't exist mid-season, so those inputs are
// empty — the projection reads slightly conservative for award-caliber teams,
// which is the right direction for a mid-season check-in.

import { idb } from "../../db/index.ts";
import { g } from "../../util/index.ts";
import {
	computeReportCard,
	type ReportCard,
} from "../../../common/seasonReportCard.ts";
import { buildReportCardInput } from "./reportCards.ts";

// Below this many games the pace math is noise, not a projection.
export const PROJECTION_MIN_GAMES = 5;

export type ProjectedReportCard = {
	card: ReportCard;
	won: number;
	lost: number;
	gp: number;
	// The extrapolated full-season record the card was scored on.
	paceWon: number;
	projectedWins: number | undefined;
};

// undefined = nothing to project yet (no team season, or too few games).
export const computeProjectedReportCard = async (
	tid: number,
	season: number,
): Promise<ProjectedReportCard | undefined> => {
	const ts = await idb.cache.teamSeasons.indexGet("teamSeasonsByTidSeason", [
		tid,
		season,
	]);
	if (!ts) {
		return;
	}
	const gp = ts.won + ts.lost + (ts.tied ?? 0) + (ts.otl ?? 0);
	if (gp < PROJECTION_MIN_GAMES) {
		return;
	}

	const numGames = g.get("numGames");
	const scale = numGames / gp;
	const teamsForMov = await idb.getCopies.teamsPlus(
		{
			attrs: ["tid"],
			stats: ["mov"],
			season,
		},
		"noCopyCache",
	);
	const mov = teamsForMov.find((t) => t.tid === tid)?.stats.mov ?? 0;
	const players = await idb.cache.players.indexGetAll("playersByTid", tid);

	const input = buildReportCardInput({
		season,
		numPlayoffRounds: g.get("numGamesPlayoffSeries", "current").length,
		won: Math.round(ts.won * scale),
		lost: Math.round(ts.lost * scale),
		avgMargin: mov,
		playoffRoundsWon: ts.playoffRoundsWon,
		madePlayIn: false,
		projectedWins: ts.projectedWins,
		players,
		awards: [],
		lotteryPick: null,
		apron: ts.apron,
		priorApron: undefined,
		payrollEndOfSeason: undefined,
		luxuryPayroll: g.get("luxuryPayroll"),
	});

	return {
		card: computeReportCard(input),
		won: ts.won,
		lost: ts.lost,
		gp,
		paceWon: Math.round(ts.won * scale),
		projectedWins: ts.projectedWins,
	};
};

export default computeProjectedReportCard;
